'use client'
import * as React from 'react'
import {
  ArrowRight,
  Check,
  ChevronDown,
  Download,
  Heart,
  Loader2,
  Lock,
  Plus,
  Share2,
  X,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import type { AuditResult, PostCard } from '@/app/api/brand-audit/route'

// ─────────────────────────────────────────────────────────────────────────────
// Brand Content Audit
//
// Four phases:
//  1. Input — brand name, handles, hashtags, optional employee / influencer lists
//  2. Lead gate — name / email / company, captured BEFORE the scan runs. The
//     live path can fan out up to 1000 real RapidAPI calls per scan (see
//     lib/brand-audit/live.ts), and this page is public + unauthenticated —
//     requiring a lead upfront is real friction against automated abuse, on
//     top of the server-side rate limiter in app/api/brand-audit/route.ts.
//  3. Scanning — animated progress steps; real UGC thumbnails swap in as soon
//     as the (now real, potentially long-running) fetch starts returning data
//  4. Unlocked — full result + post grid, no further gate
// ─────────────────────────────────────────────────────────────────────────────

type Phase = 'input' | 'leadgate' | 'scanning' | 'unlocked'
type Category = 'all' | 'customer' | 'influencer' | 'employee' | 'brand' | 'brand_repost'
type SortOrder = 'likes' | 'recent'

const CATEGORY_META: Record<
  Exclude<Category, 'all'>,
  { label: string; color: string; bg: string; bar: string; dot: string }
> = {
  customer:     { label: 'Customer UGC',       color: 'text-emerald-700', bg: 'bg-emerald-50',  bar: 'bg-emerald-500', dot: 'bg-emerald-500'  },
  influencer:   { label: 'Influencer & brand collabs', color: 'text-violet-700',  bg: 'bg-violet-50',   bar: 'bg-violet-500',  dot: 'bg-violet-500'   },
  employee:     { label: 'Employee content',   color: 'text-sky-700',     bg: 'bg-sky-50',      bar: 'bg-sky-500',     dot: 'bg-sky-500'      },
  brand:        { label: 'Brand own posts',    color: 'text-amber-700',   bg: 'bg-amber-50',    bar: 'bg-amber-500',   dot: 'bg-amber-500'    },
  // Short-label (`.split(' ')[0]`) deliberately reads "Reposted", not
  // "Brand" — keeps it visually distinct from the brand-original chip on
  // post tiles and filter buttons instead of both collapsing to "Brand".
  brand_repost: { label: 'Reposted UGC',       color: 'text-orange-700',  bg: 'bg-orange-50',   bar: 'bg-orange-500',  dot: 'bg-orange-500'   },
}

function compact(n: number): string {
  if (n < 1_000)         return String(n)
  if (n < 1_000_000)     return (n / 1_000).toFixed(1).replace(/\.0$/, '') + 'K'
  return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M'
}

function money(n: number): string {
  if (n >= 1_000_000) return '$' + (n / 1_000_000).toFixed(1) + 'M'
  if (n >= 1_000)     return '$' + (n / 1_000).toFixed(0) + 'K'
  return '$' + n
}

// ─── Scanning steps ───────────────────────────────────────────────────────────
function buildSteps(handles: string[], hashtags: string[]) {
  return [
    ...handles.map((h) => `Scanning ${h}…`),
    ...hashtags.map((h) => `Searching ${h}…`),
    'Classifying content…',
    'Mapping influencer posts…',
    'Estimating organic reach…',
    'Building your report…',
  ]
}

// ─── Server-Sent Events reader ────────────────────────────────────────────────
// /api/brand-audit streams `event: <name>\ndata: <json>\n\n` frames so the
// client can show real progress (calls made, posts fetched, real thumbnails)
// while a deep live scan is still running. Reads the response body directly
// rather than EventSource (which is GET-only and can't carry our POST body).
async function consumeSSE(res: Response, onEvent: (event: string, data: any) => void) {
  const reader = res.body?.getReader()
  if (!reader) return
  const decoder = new TextDecoder()
  let buffer = ''
  while (true) {
    const { value, done } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    let sep: number
    while ((sep = buffer.indexOf('\n\n')) !== -1) {
      const raw = buffer.slice(0, sep)
      buffer = buffer.slice(sep + 2)
      let event = 'message'
      let dataStr = ''
      for (const line of raw.split('\n')) {
        if (line.startsWith('event:')) event = line.slice(6).trim()
        else if (line.startsWith('data:')) dataStr += line.slice(5).trim()
      }
      if (!dataStr) continue
      try { onEvent(event, JSON.parse(dataStr)) } catch { /* ignore malformed frame */ }
    }
  }
}

// ─── Category bar row ─────────────────────────────────────────────────────────
function CategoryBar({
  cat,
  count,
  pct,
  animate,
}: {
  cat: Exclude<Category, 'all'>
  count: number
  pct: number
  animate: boolean
}) {
  const meta = CATEGORY_META[cat]
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-2 text-sm font-medium">
          <span className={cn('h-2 w-2 shrink-0 rounded-full', meta.dot)} />
          <span>{meta.label}</span>
        </span>
        <span className="tabular-nums text-sm font-semibold">
          {compact(count)} <span className="font-normal text-muted-foreground">({pct}%)</span>
        </span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-secondary">
        <div
          className={cn('h-full rounded-full', meta.bar)}
          style={{
            width: animate ? `${pct}%` : '0%',
            transition: animate ? `width 800ms var(--ease-out)` : 'none',
          }}
        />
      </div>
    </div>
  )
}

// ─── Post card ────────────────────────────────────────────────────────────────
function PostTile({ post, blur }: { post: PostCard; blur?: boolean }) {
  const meta = CATEGORY_META[post.category]
  const initial = post.handle.replace(/^[@#]/, '')[0]?.toUpperCase() ?? 'U'
  const avatarHue = (post.hue + 150) % 360
  const Wrapper: React.ElementType = post.permalink && !blur ? 'a' : 'div'
  const wrapperProps = post.permalink && !blur
    ? { href: post.permalink, target: '_blank', rel: 'noopener noreferrer' }
    : {}
  return (
    <Wrapper
      {...wrapperProps}
      className={cn('group relative block overflow-hidden rounded-xl border shadow-sm', blur && 'select-none')}
    >
      <div
        className="relative aspect-[4/5] w-full"
        style={{
          background: `linear-gradient(155deg, hsl(${post.hue} 50% 60%) 0%, hsl(${(post.hue + 45) % 360} 46% 34%) 100%)`,
          filter: blur ? 'blur(8px)' : undefined,
        }}
      >
        {post.imageUrl && (
          // eslint-disable-next-line @next/next/no-img-element -- proxied IG CDN url, next/image can't optimize it
          <img
            src={post.imageUrl}
            alt=""
            loading="lazy"
            referrerPolicy="no-referrer"
            className="absolute inset-0 h-full w-full object-cover"
            onError={(e) => { e.currentTarget.style.display = 'none' }}
          />
        )}
        {!blur && (
          <>
            {/* Header: avatar + handle + category */}
            <div
              className="absolute inset-x-0 top-0 flex items-center gap-2 px-3 py-2.5"
              style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.52) 0%, transparent 100%)' }}
            >
              <div
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-white/60 text-[9px] font-bold text-white"
                style={{ background: `hsl(${avatarHue} 58% 48%)` }}
              >
                {initial}
              </div>
              <span className="truncate text-[10px] font-semibold text-white">{post.handle}</span>
              <span className={cn('ml-auto shrink-0 rounded px-1.5 py-0.5 text-[8px] font-bold', meta.bg, meta.color)}>
                {meta.label.split(' ')[0]}
              </span>
            </div>
            {/* Footer: caption + stats */}
            <div
              className="absolute inset-x-0 bottom-0 px-3 pb-3 pt-8"
              style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.65) 0%, transparent 100%)' }}
            >
              <p className="line-clamp-2 text-[10px] leading-relaxed text-white/90">{post.caption}</p>
              <div className="mt-1 flex items-center gap-1 text-[9px] text-white/70">
                <Heart className="h-2.5 w-2.5 shrink-0" />
                <span>{compact(post.likes)}</span>
                {post.followers > 0 && (
                  <>
                    <span className="mx-1 opacity-50">·</span>
                    <span>{compact(post.followers)} followers</span>
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>
      {blur && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/40 backdrop-blur-sm">
          <Lock className="h-6 w-6 text-foreground/70" />
        </div>
      )}
    </Wrapper>
  )
}

// ─── Lead field ───────────────────────────────────────────────────────────────
function LeadField({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  full,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  type?: string
  placeholder?: string
  full?: boolean
}) {
  return (
    <label className={cn('block', full && 'sm:col-span-2')}>
      <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
      <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
    </label>
  )
}

// ─── Dynamic handle list ──────────────────────────────────────────────────────
function DynamicList({
  label,
  placeholder,
  max,
  items,
  onChange,
}: {
  label: string
  placeholder: string
  max: number
  items: string[]
  onChange: (items: string[]) => void
}) {
  return (
    <div className="space-y-2">
      <span className="block text-sm font-medium">{label}</span>
      {items.map((v, i) => (
        <div key={i} className="flex items-center gap-2">
          <Input
            value={v}
            onChange={(e) => onChange(items.map((x, j) => (j === i ? e.target.value : x)))}
            placeholder={placeholder}
            className="flex-1"
          />
          {items.length > 1 && (
            <button
              type="button"
              onClick={() => onChange(items.filter((_, j) => j !== i))}
              className="rounded-md p-2 text-muted-foreground transition hover:bg-secondary hover:text-foreground"
              aria-label="Remove"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      ))}
      {items.length < max && (
        <button
          type="button"
          onClick={() => onChange([...items, ''])}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
        >
          <Plus className="h-3.5 w-3.5" /> Add another
        </button>
      )}
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────
export function BrandContentAudit() {
  const [phase, setPhase] = React.useState<Phase>('input')

  // Form state
  const [brandName, setBrandName] = React.useState('')
  const [website, setWebsite] = React.useState('')
  const [handles, setHandles] = React.useState([''])
  const [hashtags, setHashtags] = React.useState([''])
  const [employeeText, setEmployeeText] = React.useState('')
  const [influencerText, setInfluencerText] = React.useState('')

  // "Start from your website" — a prefill step, not a source of truth.
  // Fetches the site and pre-fills brand name / handle / hashtag below; the
  // user still reviews and edits everything before a scan ever runs.
  const [discoverUrl, setDiscoverUrl] = React.useState('')
  const [discoverLoading, setDiscoverLoading] = React.useState(false)
  const [discoverError, setDiscoverError] = React.useState<string | null>(null)
  const [discoverApplied, setDiscoverApplied] = React.useState(false)
  // Real brand colors/font/logo, when found — kept around (not just displayed
  // once) so a later step (e.g. generating a branded proposal) can reuse it
  // without a second lookup.
  const [brandKit, setBrandKit] = React.useState<{
    primaryColor?: string; accentColor?: string; fontFamily?: string; logoUrl?: string
  } | null>(null)

  async function discoverFromUrl() {
    if (!discoverUrl.trim()) { setDiscoverError('Paste a website URL first.'); return }
    setDiscoverError(null)
    setDiscoverApplied(false)
    setBrandKit(null)
    setDiscoverLoading(true)
    try {
      const res = await fetch('/api/brand-audit/discover', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: discoverUrl.trim() }),
      })
      const data = await res.json()
      if (!data.ok) {
        setDiscoverError(data.error ?? 'Could not read that website.')
        return
      }
      const r = data.result as {
        brandName?: string; website: string; instagramHandles: string[]; suggestedHashtags: string[]
        brandKit?: { primaryColor?: string; accentColor?: string; fontFamily?: string; logoUrl?: string }
      }
      if (r.brandKit) setBrandKit(r.brandKit)
      if (r.brandName && !brandName.trim()) setBrandName(r.brandName)
      if (!website.trim()) setWebsite(r.website)
      if (r.instagramHandles.length > 0) {
        setHandles((prev) => {
          const existing = new Set(prev.map((h) => h.trim().replace(/^@/, '').toLowerCase()).filter(Boolean))
          const fresh = r.instagramHandles.filter((h) => !existing.has(h.toLowerCase()))
          const merged = [...prev.filter((h) => h.trim()), ...fresh]
          return merged.length > 0 ? merged.slice(0, 10) : ['']
        })
      }
      if (r.suggestedHashtags.length > 0) {
        setHashtags((prev) => {
          const existing = new Set(prev.map((h) => h.trim().replace(/^#/, '').toLowerCase()).filter(Boolean))
          const fresh = r.suggestedHashtags.filter((h) => !existing.has(h.toLowerCase()))
          const merged = [...prev.filter((h) => h.trim()), ...fresh]
          return merged.length > 0 ? merged.slice(0, 10) : ['']
        })
      }
      if (r.instagramHandles.length === 0) {
        setDiscoverError("Found the site, but no Instagram link on it — add the handle below manually.")
      } else {
        setDiscoverApplied(true)
      }
    } catch {
      setDiscoverError('Could not reach that website. Check the URL and try again.')
    } finally {
      setDiscoverLoading(false)
    }
  }
  const [showAdvanced, setShowAdvanced] = React.useState(false)
  const [formError, setFormError] = React.useState<string | null>(null)

  // Scanning state
  const [steps, setSteps] = React.useState<string[]>([])
  const [completedSteps, setCompletedSteps] = React.useState<number[]>([])
  const [activeStep, setActiveStep] = React.useState(0)
  const [runningCount, setRunningCount] = React.useState(0)
  const [stepLabel, setStepLabel] = React.useState('')

  // Result state
  const [result, setResult] = React.useState<AuditResult | null>(null)
  const [apiError, setApiError] = React.useState<string | null>(null)

  // Lead state
  const [firstName, setFirstName] = React.useState('')
  const [email, setEmail] = React.useState('')
  const [company, setCompany] = React.useState('')
  const [leadLoading, setLeadLoading] = React.useState(false)
  const [leadError, setLeadError] = React.useState<string | null>(null)

  // Post grid state
  const [filterCat, setFilterCat] = React.useState<Category>('all')
  const [sortOrder, setSortOrder] = React.useState<SortOrder>('likes')

  // Animate category bars after result arrives
  const [barsAnimated, setBarsAnimated] = React.useState(false)

  // Debug mode — activated by ?idk_dbg=1 in URL
  const [isDebug, setIsDebug] = React.useState(false)
  React.useEffect(() => {
    setIsDebug(window.location.search.includes('idk_dbg=1'))
  }, [])
  const [rawApiData, setRawApiData] = React.useState<Record<string, unknown> | null>(null)
  const [scanDurationMs, setScanDurationMs] = React.useState<number | null>(null)
  // Real post thumbnails, populated as soon as the live API responds — swapped
  // into the scanning grid in place of gradient placeholders so the animation
  // shows actual UGC instead of only ever looking synthetic. Empty for
  // simulated runs (no real imageUrl exists to show).
  const [liveThumbs, setLiveThumbs] = React.useState<{ imageUrl: string; handle: string }[]>([])
  // Methodology disclosure surfaced in the actual result UI (not just the
  // ?idk_dbg=1 panel) — how many real RapidAPI calls backed this report, so
  // the "N analysed" sample reads as verified rather than arbitrary.
  const [resultMeta, setResultMeta] = React.useState<{ live: boolean; apiCallCount: number } | null>(null)
  // Real running progress from the server's SSE stream (see /api/brand-audit)
  // — null until the first 'progress' event arrives. Once set, this replaces
  // the scripted warm-up animation as the source of truth for the counter,
  // percentage and thumbnails, so a long scan shows real numbers moving
  // instead of freezing on whatever the scripted animation last set.
  const [liveProgress, setLiveProgress] = React.useState<{
    callsMade: number
    callsBudget: number
    postsFetched: number
    categoryCounts?: { customer: number; influencer: number; employee: number; brand: number; brand_repost: number }
  } | null>(null)
  const liveArrivedRef = React.useRef(false)
  // Latency watchdog — flips true if no progress event has landed in a while,
  // so a genuinely slow/stalled upstream call is visibly different from one
  // that's just between pages.
  const [stalled, setStalled] = React.useState(false)
  const lastProgressAtRef = React.useRef(0)

  // Cell pool for scanning animation — deterministic from user's inputs
  const cellPool = React.useMemo(() => {
    const cleanH = handles.filter(h => h.trim()).map(h => h.startsWith('@') ? h : `@${h}`)
    const cleanT = hashtags.filter(h => h.trim()).map(h => h.startsWith('#') ? h : `#${h}`)
    const brand = brandName.trim().toLowerCase().replace(/\s+/g, '') || 'brand'
    const handlePool = [
      ...cleanH,
      `@${brand}fan`, `@${brand}_official`, '@styleinfluencer_in', '@fashionista_uk',
      '@realhappycustomer', '@shopaholic101', '@lifestyleblogger', '@fitnessmom',
      '@home_style', '@beauty.addict', '@travel_diary__', '@unboxing_daily',
    ]
    const tagPool = [
      ...cleanT,
      `#${brand}`, `#${brand}unboxing`, `#${brand}review`, `#${brand}haul`,
      '#ugccreator', '#brandlove', '#productreview', '#myfinds',
    ]
    const hues = [160,200,280,320,40,260,180,100,220,140,30,340,170,210,290,330,50,270,190,110,230,150,20,350,165,205,285,325,45,265,185,105,225,145,35,345,175,215,295,335,155,195]
    const fakeLikes = [842,3201,521,12400,1876,443,9800,2340,677,15200,320,4500,1230,8900,560,3400,1100,6700,890,2100,1450,5600,740,3900,280,7800,1560,420,11300,2780,630,8200,1920,480,6100,1340,3700,950,4200,1680]
    return Array.from({ length: 40 }, (_, i) => {
      const useTag = tagPool.length > 0 && i % 5 === 0
      const source = useTag
        ? tagPool[Math.floor(i / 5) % tagPool.length]
        : handlePool[i % handlePool.length]
      const hue = hues[i % hues.length]
      const likes = fakeLikes[i % fakeLikes.length]
      const avatarHue = (hue + 150 + i * 23) % 360
      const initial = source.replace(/^[@#]/, '')[0]?.toUpperCase() ?? 'U'
      return { source, hue, likes, avatarHue, initial }
    })
  }, [handles, hashtags, brandName])

  // Step 1 → 2: validate the brand form, then move to lead capture (no scan yet)
  function goToLeadGate() {
    const cleanHandles = handles.filter((h) => h.trim())
    const cleanHashtags = hashtags.filter((h) => h.trim())
    if (!brandName.trim()) { setFormError('Enter your brand name.'); return }
    if (!cleanHandles.length && !cleanHashtags.length) {
      setFormError('Add at least one Instagram handle or hashtag.')
      return
    }
    setFormError(null)
    setLeadError(null)
    setPhase('leadgate')
  }

  // Step 2 → 3: capture the lead, then run the actual (potentially expensive) scan
  async function submitLeadAndScan() {
    if (!firstName.trim() || !email.trim() || !company.trim()) {
      setLeadError('Please fill every field.')
      return
    }
    if (!email.includes('@')) { setLeadError('Enter a valid email.'); return }
    setLeadError(null)
    setLeadLoading(true)

    fetch('/api/leads', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: firstName,
        email,
        company,
        source: '/tools/brand-content-audit',
        intent: 'brand-audit-scan',
        message: `Requested brand audit for: ${brandName.trim()}`,
      }),
    }).catch(() => { /* non-fatal — never block the scan on this */ })

    setLeadLoading(false)
    await startScan()
  }

  // Step 3: run the scan. Only reachable after lead capture — startScan itself
  // doesn't re-validate the brand form (goToLeadGate already did).
  async function startScan() {
    const cleanHandles = handles.filter((h) => h.trim())
    const cleanHashtags = hashtags.filter((h) => h.trim())

    const scanSteps = buildSteps(cleanHandles, cleanHashtags)
    setSteps(scanSteps)
    setCompletedSteps([])
    setActiveStep(0)
    setRunningCount(0)
    setStepLabel('')
    setApiError(null)
    setLiveThumbs([])
    setLiveProgress(null)
    setStalled(false)
    liveArrivedRef.current = false
    lastProgressAtRef.current = Date.now()
    setPhase('scanning')

    // Purely cosmetic warm-up numbers for the first ~4s before real data
    // arrives. The moment a real 'progress' event lands (liveArrivedRef),
    // this scripted animation stops updating and real counts take over —
    // for simulated/GraphQL runs (no progress events) it plays out in full.
    const stepPostCounts = [
      ...cleanHandles.map(() => Math.round(500 + Math.random() * 2500)),
      ...cleanHashtags.map(() => Math.round(200 + Math.random() * 1000)),
      ...Array(4).fill(0), // classify, map, estimate, build
    ]

    // Watchdog: if no progress event lands for 8s while a real scan is in
    // flight, surface that the upstream is slow rather than looking frozen.
    const watchdog = setInterval(() => {
      if (liveArrivedRef.current && Date.now() - lastProgressAtRef.current > 8000) {
        setStalled(true)
      }
    }, 2000)

    const apiCallStart = performance.now()
    let finalData: any = null
    const streamDone = (async () => {
      try {
        const res = await fetch('/api/brand-audit', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            brandName: brandName.trim(),
            website: website.trim() || undefined,
            handles: cleanHandles,
            hashtags: cleanHashtags,
            employeeHandles: employeeText.split('\n').map((s) => s.trim()).filter(Boolean),
            influencerHandles: influencerText.split('\n').map((s) => s.trim()).filter(Boolean),
            leadEmail: email.trim(),
          }),
        })
        await consumeSSE(res, (event, data) => {
          if (event === 'progress') {
            liveArrivedRef.current = true
            lastProgressAtRef.current = Date.now()
            setStalled(false)
            setLiveProgress(data)
            if (Array.isArray(data.images) && data.images.length > 0) {
              setLiveThumbs((prev) => {
                const seen = new Set(prev.map((t) => t.imageUrl))
                const merged = [...prev]
                for (const img of data.images) {
                  if (!seen.has(img.imageUrl)) { merged.push(img); seen.add(img.imageUrl) }
                }
                return merged.slice(0, 40)
              })
            }
          } else if (event === 'done') {
            finalData = data
          }
        })
      } catch (err) {
        finalData = { ok: false, error: err instanceof Error ? err.message : 'Connection lost. Please try again.' }
      }
    })()

    // Scripted warm-up: steps with ~700ms gaps, minimum 4s total — skips its
    // own updates the moment real progress data starts arriving.
    const stepDelay = Math.max(700, (4500 - 500) / scanSteps.length)
    for (let i = 0; i < scanSteps.length; i++) {
      await new Promise((r) => setTimeout(r, i === 0 ? 400 : stepDelay))
      if (liveArrivedRef.current) break
      setActiveStep(i + 1)
      setCompletedSteps((prev) => [...prev, i])
      const added = stepPostCounts[i] ?? 0
      if (added > 0) {
        setRunningCount((prev) => prev + added)
        setStepLabel(scanSteps[i].replace('…', ''))
      }
    }
    if (!liveArrivedRef.current) {
      setActiveStep(scanSteps.length)
      setCompletedSteps(scanSteps.map((_, i) => i))
    }

    await streamDone
    clearInterval(watchdog)
    setStalled(false)
    const data = finalData ?? { ok: false, error: 'No response from server. Please try again.' }
    setScanDurationMs(Math.round(performance.now() - apiCallStart))
    setRawApiData(data)

    if (!data.ok || !data.result) {
      setApiError(data.error ?? 'Something went wrong. Please try again.')
      setPhase('leadgate')
      return
    }

    setResult(data.result)
    const debugMeta = data._debug as { rapidApiCalls?: unknown[] } | undefined
    setResultMeta({
      live: data.result.dataSource === 'live',
      apiCallCount: Array.isArray(debugMeta?.rapidApiCalls) ? debugMeta.rapidApiCalls.length : 0,
    })

    // Lead is already captured (this only runs after submitLeadAndScan) — save
    // the proposal for the rep, fire-and-forget, never blocks unlocking.
    fetch('/api/brand-audit/proposal', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        lead: { firstName, email, company },
        audit: data.result,
        inputSummary: { handles: cleanHandles, hashtags: cleanHashtags },
      }),
    }).catch(() => { /* non-fatal */ })

    setPhase('unlocked')
    // Trigger bar animation after mount
    setTimeout(() => setBarsAnimated(true), 100)
  }

  function exportCSV() {
    if (!result) return
    const rows = [
      ['Handle', 'Category', 'Followers', 'Likes', 'Caption'],
      ...result.topPosts.map((p) => [
        p.handle,
        CATEGORY_META[p.category].label,
        String(p.followers),
        String(p.likes),
        `"${p.caption.replace(/"/g, '""')}"`,
      ]),
    ]
    const csv = rows.map((r) => r.join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${result.slug}-audit.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  function copyShareLink() {
    if (!result) return
    navigator.clipboard.writeText(
      `${window.location.origin}/tools/brand-content-audit/report/${result.slug}`
    )
  }

  const filteredPosts = React.useMemo(() => {
    if (!result) return []
    const posts = filterCat === 'all' ? result.topPosts : result.topPosts.filter((p) => p.category === filterCat)
    return sortOrder === 'likes' ? [...posts].sort((a, b) => b.likes - a.likes) : posts
  }, [result, filterCat, sortOrder])

  // ── Phase: Input ─────────────────────────────────────────────────────────────
  if (phase === 'input') {
    return (
      <div className="rounded-2xl border bg-card text-foreground shadow-xl">
        <div className="border-b px-6 py-4 md:px-8">
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
            Step 1 of 1 — define your brand
          </p>
        </div>
        <div className="space-y-6 p-6 md:p-8">
          {/* Start from your website — prefills the fields below, never runs anything on its own */}
          <div className="rounded-xl border border-dashed border-primary/30 bg-primary/5 p-4">
            <label className="block text-sm font-medium" htmlFor="discover-url">
              Start from your website <span className="font-normal text-muted-foreground">(optional)</span>
            </label>
            <p className="mt-0.5 text-xs text-muted-foreground">
              We&apos;ll look for your Instagram link and pre-fill the fields below — review before running the scan.
            </p>
            <div className="mt-2 flex flex-col gap-2 sm:flex-row">
              <Input
                id="discover-url"
                placeholder="yourbrand.com"
                value={discoverUrl}
                onChange={(e) => { setDiscoverUrl(e.target.value); setDiscoverApplied(false) }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); discoverFromUrl() } }}
              />
              <Button
                type="button"
                variant="outline"
                onClick={discoverFromUrl}
                disabled={discoverLoading}
                className="shrink-0 gap-1.5"
              >
                {discoverLoading ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Looking…</>
                ) : (
                  'Fetch details'
                )}
              </Button>
            </div>
            {discoverError && <p className="mt-2 text-xs text-destructive">{discoverError}</p>}
            {discoverApplied && !discoverError && (
              <p className="mt-2 text-xs font-medium text-emerald-600">Filled in below — check it over.</p>
            )}
            {brandKit && (brandKit.primaryColor || brandKit.accentColor || brandKit.logoUrl) && (
              <div className="mt-2 flex items-center gap-2 rounded-lg border border-border/60 bg-card px-2.5 py-1.5">
                {brandKit.logoUrl && (
                  // eslint-disable-next-line @next/next/no-img-element -- external brand logo, not optimizable
                  <img src={brandKit.logoUrl} alt="" className="h-5 w-5 rounded object-contain" onError={(e) => { e.currentTarget.style.display = 'none' }} />
                )}
                {brandKit.primaryColor && (
                  <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
                    <span className="h-3 w-3 rounded-full border border-border/60" style={{ background: brandKit.primaryColor }} />
                    {brandKit.primaryColor}
                  </span>
                )}
                {brandKit.accentColor && (
                  <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
                    <span className="h-3 w-3 rounded-full border border-border/60" style={{ background: brandKit.accentColor }} />
                    {brandKit.accentColor}
                  </span>
                )}
                {brandKit.fontFamily && (
                  <span className="text-[11px] text-muted-foreground">{brandKit.fontFamily}</span>
                )}
                <span className="ml-auto text-[10px] uppercase tracking-wider text-muted-foreground/70">brand kit found</span>
              </div>
            )}
          </div>

          {/* Brand name + website */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium" htmlFor="brand-name">
                Brand name
              </label>
              <Input
                id="brand-name"
                className="mt-1.5"
                placeholder="e.g. Myntra"
                value={brandName}
                onChange={(e) => setBrandName(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium" htmlFor="brand-website">
                Website <span className="font-normal text-muted-foreground">(optional)</span>
              </label>
              <Input
                id="brand-website"
                className="mt-1.5"
                placeholder="myntra.com"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
              />
            </div>
          </div>

          {/* Instagram handles */}
          <DynamicList
            label="Instagram handles (up to 10)"
            placeholder="@yourbrand"
            max={10}
            items={handles}
            onChange={setHandles}
          />

          {/* Hashtags */}
          <DynamicList
            label="Hashtags to scan (up to 10)"
            placeholder="#yourbrand"
            max={10}
            items={hashtags}
            onChange={setHashtags}
          />

          {/* Advanced / optional */}
          <div>
            <button
              type="button"
              onClick={() => setShowAdvanced((v) => !v)}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition"
              style={{ transition: `color var(--dur-fast) var(--ease-out)` }}
            >
              <ChevronDown
                className={cn('h-4 w-4 shrink-0 transition-transform', showAdvanced && 'rotate-180')}
                style={{ transition: `transform var(--dur) var(--ease-spring)` }}
              />
              Advanced: employee + influencer lists (optional)
            </button>

            {showAdvanced && (
              <div className="mt-4 space-y-4 rounded-xl border bg-secondary/30 p-4">
                <div>
                  <label className="block text-sm font-medium" htmlFor="emp-handles">
                    Employee handles <span className="text-muted-foreground font-normal">(one per line)</span>
                  </label>
                  <textarea
                    id="emp-handles"
                    rows={3}
                    placeholder={'@teamhandle1\n@teamhandle2'}
                    value={employeeText}
                    onChange={(e) => setEmployeeText(e.target.value)}
                    className="mt-1.5 w-full rounded-md border bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium" htmlFor="inf-handles">
                    Known influencer handles <span className="text-muted-foreground font-normal">(one per line)</span>
                  </label>
                  <textarea
                    id="inf-handles"
                    rows={3}
                    placeholder={'@influencer1\n@influencer2'}
                    value={influencerText}
                    onChange={(e) => setInfluencerText(e.target.value)}
                    className="mt-1.5 w-full rounded-md border bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
                  />
                </div>
              </div>
            )}
          </div>

          {(formError || apiError) && (
            <p className="text-sm text-destructive">{formError ?? apiError}</p>
          )}

          <Button onClick={goToLeadGate} className="w-full sm:w-auto" size="lg">
            Run Free Audit <ArrowRight className="ml-1.5 h-4 w-4" />
          </Button>

          <p className="text-xs text-muted-foreground">
            Scans public Instagram data. Takes a few seconds to a few minutes for large accounts.
          </p>
        </div>
      </div>
    )
  }

  // ── Phase: Lead gate (before the scan runs) ──────────────────────────────────
  if (phase === 'leadgate') {
    return (
      <div className="rounded-2xl border bg-card text-foreground shadow-xl">
        <div className="border-b px-6 py-4 md:px-8">
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
            Step 2 of 2 — where should we send the report?
          </p>
        </div>
        <div className="p-6 md:p-8">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Lock className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-display text-lg font-semibold">
                Tell us where to send the results
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                The scan pulls real, live Instagram data for {brandName.trim() || 'your brand'} — it can take a
                few seconds up to a few minutes for large accounts. We&apos;ll email you a link to the report either way.
              </p>
            </div>
          </div>

          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            <LeadField
              label="First name"
              value={firstName}
              onChange={setFirstName}
              placeholder="Maya"
            />
            <LeadField
              label="Business email"
              value={email}
              onChange={setEmail}
              type="email"
              placeholder="maya@brand.com"
            />
            <LeadField
              label="Company name"
              value={company}
              onChange={setCompany}
              placeholder="Your company"
              full
            />
          </div>

          {(leadError || apiError) && (
            <p className="mt-3 text-sm text-destructive">{leadError ?? apiError}</p>
          )}

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Button onClick={submitLeadAndScan} disabled={leadLoading}>
              {leadLoading ? (
                <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> One moment…</>
              ) : (
                <>Run Free Audit <ArrowRight className="ml-1.5 h-4 w-4" /></>
              )}
            </Button>
            <button
              type="button"
              onClick={() => { setPhase('input'); setApiError(null) }}
              className="text-xs font-medium text-muted-foreground hover:text-foreground"
            >
              ← Back
            </button>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            We never sell or share contact details.
          </p>
        </div>
      </div>
    )
  }

  // ── Phase: Scanning ──────────────────────────────────────────────────────────
  const IDUKKI_TIPS = [
    { label: 'Did you know?', text: 'Brands using Idukki see up to 4× more conversions from UGC galleries compared to standard product images.' },
    { label: 'Pro tip', text: 'Tag products directly inside a UGC post — customers can add to cart without ever leaving the gallery.' },
    { label: 'Did you know?', text: 'Idukki\'s Super Search lets you find the right UGC in seconds using AI-powered filters across all connected platforms.' },
    { label: 'Pro tip', text: 'You can request usage rights from creators in one click — Idukki handles the DM and tracks consent automatically.' },
    { label: 'Did you know?', text: 'Idukki connects to Instagram, TikTok, YouTube, LinkedIn, Google Reviews, Trustpilot, and more — one dashboard for all your UGC.' },
    { label: 'Pro tip', text: 'Shoppable video widgets load asynchronously, so adding one to your store has zero impact on page speed scores.' },
    { label: 'Did you know?', text: 'Collab posts — where a creator and your brand co-author a reel — are one of the fastest-growing influencer formats on Instagram right now.' },
  ]

  if (phase === 'scanning') {
    // Once real progress events start arriving, they're the source of truth
    // for the counter and percentage — the scripted warm-up animation below
    // stops updating at that point (see startScan). callsMade drives an
    // asymptotic curve (never claims a false "done" until the real 'done'
    // event lands) rather than a literal ratio against the 1000-call budget,
    // since most scans finish in a fraction of that budget and a literal
    // ratio would look stuck near 0%.
    const pct = liveProgress
      ? Math.min(96, Math.round(100 * (1 - Math.exp(-liveProgress.callsMade / 15))))
      : steps.length > 0 ? Math.round((completedSteps.length / steps.length) * 100) : 0
    const displayCount = liveProgress ? liveProgress.postsFetched : runningCount
    const displayLabel = liveProgress
      ? `${liveProgress.callsMade} real Instagram API call${liveProgress.callsMade === 1 ? '' : 's'}${stalled ? ' · upstream is slow, still trying…' : ''}`
      : stepLabel
    const tipIndex = Math.min(Math.floor(completedSteps.length / 1.5), IDUKKI_TIPS.length - 1)
    const tip = IDUKKI_TIPS[tipIndex]
    const currentStep = steps[activeStep] ?? steps[steps.length - 1] ?? 'Processing…'

    // Derive clean sources for the chip strip
    const srcHandles = handles.filter(h => h.trim()).map(h => h.startsWith('@') ? h : `@${h}`)
    const srcHashtags = hashtags.filter(h => h.trim()).map(h => h.startsWith('#') ? h : `#${h}`)
    const allSources = [...srcHandles, ...srcHashtags]

    const cellCats = ['customer','customer','customer','customer','customer','customer','customer','customer','customer','influencer','influencer','influencer','influencer','influencer','influencer','employee','employee','employee','employee','brand','brand','brand','brand','customer','customer','influencer','influencer','employee','brand','customer','customer','influencer','employee','brand','customer','influencer','customer','influencer','employee','brand','customer']
    const catDotMap = { customer: 'bg-emerald-500', influencer: 'bg-violet-500', employee: 'bg-sky-500', brand: 'bg-amber-500' }

    return (
      <div className="rounded-2xl border bg-card text-foreground shadow-xl overflow-hidden">
        {/* Top accent bar that fills as scan progresses */}
        <div className="h-1 w-full bg-secondary">
          <div
            className="h-full bg-primary"
            style={{
              width: `${pct}%`,
              transition: `width 600ms var(--ease-out)`,
            }}
          />
        </div>

        <div className="p-6 md:p-8">
          {/* Header + live counter */}
          <div className="mb-5 flex flex-wrap items-end gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Loader2 className="h-4 w-4 shrink-0 text-primary animate-spin" style={{ animationDuration: '1s' }} />
                <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                  {pct < 65 ? 'Fetching posts…' : pct < 90 ? 'Classifying content…' : 'Building report…'}
                </p>
                {liveThumbs.length > 0 && (
                  <span
                    className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-600"
                    style={{ animation: 'tipFadeIn 300ms var(--ease-out)' }}
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                    Live Instagram data
                  </span>
                )}
              </div>
              <p
                key={displayCount}
                className="font-display text-5xl font-bold tabular-nums leading-none"
                style={{ animation: displayCount > 0 ? 'countPop 350ms var(--ease-out)' : 'none' }}
              >
                {compact(displayCount)}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                {liveProgress ? 'real posts fetched' : 'posts collected'}
                {displayLabel && (
                  <span key={displayLabel} className="ml-1" style={{ animation: 'tipFadeIn 300ms var(--ease-out)' }}>
                    · {liveProgress ? displayLabel : `from ${displayLabel}`}
                  </span>
                )}
              </p>
            </div>

            {/* Mini progress pill */}
            <div className="ml-auto flex flex-col items-end gap-1">
              <span className="text-2xl font-bold tabular-nums text-foreground">{pct}%</span>
              <span className="text-xs text-muted-foreground">complete</span>
            </div>
          </div>

          {/* Source scanning chips — shows actual handles + hashtags with live status */}
          {allSources.length > 0 && (
            <div className="mb-3 flex flex-wrap gap-1.5">
              {allSources.map((source, i) => {
                const isDone = completedSteps.includes(i)
                const isActive = activeStep === i && !isDone
                return (
                  <span
                    key={source}
                    className={cn(
                      'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold transition-all',
                      isDone
                        ? 'border-primary/30 bg-primary/10 text-primary'
                        : isActive
                        ? 'border-border bg-secondary text-foreground'
                        : 'border-border/40 text-muted-foreground/60'
                    )}
                    style={{ transition: 'all 300ms var(--ease-out)' }}
                  >
                    {isDone && <Check className="h-2.5 w-2.5" />}
                    {isActive && <Loader2 className="h-2.5 w-2.5 animate-spin" />}
                    {source}
                  </span>
                )
              })}
            </div>
          )}

          {/* Animated mini post thumbnails — styled as Instagram-like content */}
          <div className="mb-4">
            <div className="grid grid-cols-8 gap-1 sm:grid-cols-10">
              {Array.from({ length: 40 }).map((_, i) => {
                const loadAt = (i / 40) * 82
                const spinAt = loadAt - 12
                const isLoaded = pct > loadAt
                const isSpinning = pct > spinAt && !isLoaded
                const isClassified = pct > 68 && isLoaded
                const catKey = cellCats[i % cellCats.length] as keyof typeof catDotMap
                const catDot = catDotMap[catKey]
                const cell = cellPool[i]
                // Real photos take priority over the gradient placeholder once available —
                // one thumb per cell (no repeats), leaving remaining cells synthetic.
                const liveThumb = isLoaded && i < liveThumbs.length ? liveThumbs[i] : null
                return (
                  <div
                    key={i}
                    className="relative aspect-square overflow-hidden rounded-md border border-border/40"
                  >
                    {/* Pending */}
                    {!isLoaded && !isSpinning && (
                      <div className="absolute inset-0 bg-secondary/60" />
                    )}
                    {/* Spinning — fetching thumbnail */}
                    {isSpinning && (
                      <div className="absolute inset-0 bg-secondary flex items-center justify-center">
                        <Loader2 className="h-2.5 w-2.5 animate-spin text-primary/60" />
                      </div>
                    )}
                    {/* Loaded + a real photo has arrived — swap the placeholder for the actual post */}
                    {isLoaded && liveThumb && (
                      <div
                        className="absolute inset-0"
                        style={{ animation: 'cellReveal 320ms var(--ease-spring)' }}
                      >
                        <img
                          src={liveThumb.imageUrl}
                          alt=""
                          loading="lazy"
                          referrerPolicy="no-referrer"
                          className="absolute inset-0 h-full w-full object-cover"
                          onError={() => {
                            // Broken/expired CDN url (proxy hiccup, hotlink block) — drop it
                            // rather than leaving a permanently blank cell; the gradient
                            // placeholder takes over on the next render for this index.
                            setLiveThumbs((prev) => prev.filter((t) => t.imageUrl !== liveThumb.imageUrl))
                          }}
                        />
                        <div
                          className="absolute bottom-0 left-0 right-0 px-[2px] pb-[2px] pt-[4px]"
                          style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.6), transparent)' }}
                        >
                          <span
                            style={{
                              fontSize: '4px',
                              color: 'rgba(255,255,255,0.9)',
                              display: 'block',
                              overflow: 'hidden',
                              whiteSpace: 'nowrap',
                              textOverflow: 'ellipsis',
                              lineHeight: 1,
                            }}
                          >
                            {liveThumb.handle}
                          </span>
                        </div>
                      </div>
                    )}
                    {/* Loaded, no real photo yet (or simulated run) — synthetic placeholder */}
                    {isLoaded && !liveThumb && (
                      <div
                        className="absolute inset-0"
                        style={{
                          background: `linear-gradient(155deg, hsl(${cell.hue} 52% 62%) 0%, hsl(${(cell.hue + 40) % 360} 48% 36%) 100%)`,
                          animation: 'cellReveal 320ms var(--ease-spring)',
                        }}
                      >
                        {/* Profile avatar circle — top left */}
                        <div
                          className="absolute left-[3px] top-[3px] flex h-[9px] w-[9px] items-center justify-center rounded-full border border-white/70"
                          style={{ background: `hsl(${cell.avatarHue} 65% 58%)` }}
                        >
                          <span style={{ fontSize: '4px', color: '#fff', fontWeight: 700, lineHeight: 1 }}>
                            {cell.initial}
                          </span>
                        </div>
                        {/* Heart + like count — top right */}
                        <div
                          className="absolute right-[2px] top-[3px] flex items-center"
                          style={{ gap: '1px' }}
                        >
                          <span style={{ fontSize: '4px', color: 'rgba(255,255,255,0.88)', lineHeight: 1 }}>
                            ♥{compact(cell.likes)}
                          </span>
                        </div>
                        {/* Bottom gradient + source label */}
                        <div
                          className="absolute bottom-0 left-0 right-0 px-[2px] pb-[2px] pt-[4px]"
                          style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.55), transparent)' }}
                        >
                          <span
                            style={{
                              fontSize: '4px',
                              color: 'rgba(255,255,255,0.85)',
                              display: 'block',
                              overflow: 'hidden',
                              whiteSpace: 'nowrap',
                              textOverflow: 'ellipsis',
                              lineHeight: 1,
                            }}
                          >
                            {cell.source}
                          </span>
                        </div>
                      </div>
                    )}
                    {/* Category classification dot — appears after 68% */}
                    {isClassified && (
                      <div
                        className={cn('absolute bottom-[3px] right-[3px] h-[5px] w-[5px] rounded-full border border-white/60', catDot)}
                        style={{ animation: 'cellReveal 300ms var(--ease-spring)' }}
                      />
                    )}
                  </div>
                )
              })}
            </div>
          </div>

          {/* Classification breakdown — real counts from the server's live
              category tally (see categoryCounts in lib/brand-audit/live.ts),
              not a fabricated split. Only renders once real data exists,
              which for a live scan means at least one handle/hashtag branch
              has finished classifying its posts — nothing shown before that,
              rather than guessing at a fake distribution in the meantime.
              Simulated runs never populate categoryCounts, so this section
              simply never appears for them. */}
          {liveProgress?.categoryCounts && Object.values(liveProgress.categoryCounts).some((n) => n > 0) && (
            <div className="mb-4 space-y-1" style={{ animation: 'tipFadeIn 400ms var(--ease-out)' }}>
              <p className="mb-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                Content classification (real, as classified so far)
              </p>
              {(() => {
                const counts = liveProgress.categoryCounts!
                const total = Math.max(1, counts.customer + counts.influencer + counts.employee + counts.brand + counts.brand_repost)
                return (
                  [
                    { cat: 'customer' as const,     label: 'Customer UGC', bar: 'bg-emerald-500', dot: 'bg-emerald-500' },
                    { cat: 'influencer' as const,   label: 'Influencer',   bar: 'bg-violet-500',  dot: 'bg-violet-500'  },
                    { cat: 'employee' as const,     label: 'Employee',     bar: 'bg-sky-500',     dot: 'bg-sky-500'     },
                    { cat: 'brand' as const,        label: 'Brand',        bar: 'bg-amber-500',   dot: 'bg-amber-500'   },
                    { cat: 'brand_repost' as const, label: 'Reposted',     bar: 'bg-orange-500',  dot: 'bg-orange-500'  },
                  ]
                ).map(({ cat, label, bar, dot }) => {
                  const count = counts[cat]
                  const pctOfTotal = Math.round((count / total) * 100)
                  return (
                    <div key={cat} className="flex items-center gap-2">
                      <span className={cn('h-2 w-2 shrink-0 rounded-full', dot)} />
                      <span className="w-24 text-xs text-foreground/80">{label}</span>
                      <div className="flex-1 h-1 overflow-hidden rounded-full bg-secondary">
                        <div
                          className={cn('h-full rounded-full', bar)}
                          style={{ width: `${pctOfTotal}%`, transition: 'width 500ms var(--ease-out)' }}
                        />
                      </div>
                      <span className="w-10 text-right text-xs tabular-nums text-muted-foreground">{compact(count)}</span>
                    </div>
                  )
                })
              })()}
            </div>
          )}

          {/* Progress bar */}
          <div className="space-y-1 mb-5">
            <div className="flex items-center justify-between text-xs">
              <span className="font-medium text-foreground">{currentStep}</span>
              <span className="tabular-nums text-muted-foreground">{pct}%</span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-secondary">
              <div
                className="h-full rounded-full bg-primary"
                style={{ width: `${pct}%`, transition: `width 600ms var(--ease-out)` }}
              />
            </div>
            <div className="flex items-center gap-1 pt-0.5">
              {steps.map((_, i) => (
                <span
                  key={i}
                  className={cn('h-1 flex-1 rounded-full', completedSteps.includes(i) ? 'bg-primary' : 'bg-secondary')}
                  style={{ transition: `background var(--dur) var(--ease-out)` }}
                />
              ))}
            </div>
          </div>

          {/* Tip card — lime-accented, prominent */}
          <div
            className="relative overflow-hidden rounded-xl border border-primary/20 bg-primary/5 p-4"
            key={tipIndex}
            style={{ animation: 'tipFadeIn 400ms var(--ease-out)' }}
          >
            <div className="absolute left-0 top-0 h-full w-1 rounded-l-xl bg-brand-lime" />
            <div className="pl-3">
              <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-primary mb-1">{tip.label}</p>
              <p className="text-sm font-medium leading-relaxed text-foreground">{tip.text}</p>
            </div>
          </div>

          <p className="mt-4 text-xs text-muted-foreground">Scanning public data only · No login required</p>
        </div>
        <style>{`
          @keyframes tipFadeIn {
            from { opacity: 0; transform: translateY(6px); }
            to   { opacity: 1; transform: translateY(0); }
          }
          @keyframes countPop {
            0%   { opacity: 0.5; transform: translateY(-8px) scale(0.96); }
            60%  { transform: translateY(2px) scale(1.02); }
            100% { opacity: 1; transform: translateY(0) scale(1); }
          }
          @keyframes cellReveal {
            0%   { opacity: 0; transform: scale(0.7); }
            60%  { transform: scale(1.08); }
            100% { opacity: 1; transform: scale(1); }
          }
        `}</style>
      </div>
    )
  }

  // ── Phases: Teaser + Unlocked ────────────────────────────────────────────────
  if (!result) return null

  const cats = (Object.keys(result.categories) as Array<keyof typeof result.categories>)

  return (
    <div className="space-y-6 text-foreground">
      {/* Headline stat + restart */}
      <div className="rounded-2xl border bg-card p-6 shadow-xl md:p-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          {/* Brand identity */}
          <div className="flex items-center gap-4">
            {result.brandLogoUrl && (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={result.brandLogoUrl}
                alt={result.brandName}
                width={52}
                height={52}
                className="h-13 w-13 rounded-xl border bg-white object-contain p-1 shadow-sm"
                onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
              />
            )}
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-primary">Audit complete</p>
              <p className="mt-0.5 font-display text-xl font-bold">{result.brandName}</p>
              {result.brandDomain && (
                <p className="text-xs text-muted-foreground">{result.brandDomain}</p>
              )}
            </div>
          </div>

          <button
            type="button"
            onClick={() => { setPhase('input'); setResult(null); setBarsAnimated(false) }}
            className="text-xs font-medium text-muted-foreground hover:text-foreground transition"
            style={{ transition: `color var(--dur-fast) var(--ease-out)` }}
          >
            Run another audit
          </button>
        </div>

        {/* Headline number — leads with what was actually fetched, not the
            self-reported total, since that's the number that's real and
            verifiable rather than Instagram's own estimate. */}
        <div className="mt-5 border-t pt-5">
          <p className="font-display text-4xl font-bold tabular-nums md:text-5xl">
            {compact(result.postsAnalysed)}
            <span className="ml-2 text-xl font-normal text-muted-foreground">real posts fetched</span>
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            about <strong className="font-semibold text-foreground">{result.brandName}</strong> on Instagram
            <span className="mx-2 opacity-30">·</span>~{compact(result.totalPosts)} estimated to exist in total
          </p>
          {resultMeta && (
            <p className="mt-1.5 text-xs text-muted-foreground">
              {resultMeta.live ? (
                <span className="inline-flex items-center gap-1 font-semibold text-emerald-600">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  Live data
                  <span className="font-normal text-muted-foreground">
                    &middot; {resultMeta.apiCallCount} Instagram API call{resultMeta.apiCallCount === 1 ? '' : 's'}
                  </span>
                </span>
              ) : (
                'Estimated from public benchmarks for this scan — not live Instagram data'
              )}
            </p>
          )}
          {(() => {
            const cleanHandles = handles.filter((h) => h.trim())
            const cleanHashtags = hashtags.filter((h) => h.trim())
            const sources = [
              ...cleanHandles.map((h) => h.startsWith('@') ? h : `@${h}`),
              ...cleanHashtags.map((h) => h.startsWith('#') ? h : `#${h}`),
            ]
            if (!sources.length) return null
            return (
              <p className="mt-1.5 text-xs text-muted-foreground">
                Analysed across{' '}
                {sources.map((s, i) => (
                  <React.Fragment key={s}>
                    <span className="font-medium text-foreground/80">{s}</span>
                    {i < sources.length - 1 && <span className="mx-0.5 text-border">,</span>}{' '}
                  </React.Fragment>
                ))}
                &middot; Last 12 months
              </p>
            )
          })()}
        </div>
      </div>

      {/* Category breakdown */}
      <div className="rounded-2xl border bg-card p-6 shadow-sm md:p-8">
        <div className="flex items-center justify-between gap-2">
          <div>
            <h3 className="font-display text-lg font-semibold">Content breakdown</h3>
            <p className="mt-0.5 text-xs text-muted-foreground">Who is actually posting about {result.brandName}</p>
          </div>
          <span className="text-xs tabular-nums text-muted-foreground">{compact(result.totalPosts)} posts</span>
        </div>
        <div className="mt-5 space-y-4">
          {cats.map((cat) => (
            <CategoryBar
              key={cat}
              cat={cat}
              count={result.categories[cat].count}
              pct={result.categories[cat].pct}
              animate={barsAnimated}
            />
          ))}
        </div>
        {result.dataQuality && result.dataQuality.unknownFollowerPct > 0 && (
          <p className="mt-4 border-t pt-4 text-xs text-muted-foreground">
            Follower counts were unavailable for {result.dataQuality.unknownFollowerPct}% of posts (common on hashtag
            and tagged-photo results) — reach for those is estimated from likes instead of a verified follower count.
          </p>
        )}
      </div>

      {/* Value metrics */}
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="rounded-2xl border bg-card p-5 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">
            Estimated organic reach
          </p>
          <p className="mt-2 font-display text-3xl font-bold tabular-nums">{compact(result.estimatedReach)}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Based on creator follower counts × avg. 3% organic reach
          </p>
        </div>
        <div className="rounded-2xl border bg-card p-5 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">
            Equivalent ad value
          </p>
          <p className="mt-2 font-display text-3xl font-bold tabular-nums">{money(result.adValue)}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            What this reach would cost to buy at $3 CPM
          </p>
        </div>
      </div>

      {/* What the numbers show — data-driven, no fabricated stats */}
      <div className="rounded-2xl border bg-card p-6 shadow-sm md:p-8">
        <h3 className="font-display text-lg font-semibold">What the numbers show</h3>
        <div className="mt-5 divide-y divide-border/60">
          {[
            {
              stat: `${result.categories.customer.pct}%`,
              headline: 'unprompted customer content',
              detail: `Of everything posted about ${result.brandName}, that fraction came from real customers — not your marketing team, not paid partnerships.`,
            },
            {
              stat: compact(result.postsAnalysed),
              headline: 'posts analysed and ready to make shoppable',
              detail: 'Each one can be tagged to a product page and embedded as a one-click purchase gallery. Rights requests go out automatically via DM.',
            },
            {
              stat: compact(result.estimatedReach),
              headline: 'estimated reach sitting unclaimed',
              detail: `The combined audience behind every creator who posted about ${result.brandName}. Reusable the moment you request and receive rights.`,
            },
          ].map((item) => (
            <div key={item.headline} className="flex items-start gap-5 py-4 first:pt-0 last:pb-0">
              <div className="w-16 shrink-0 pt-0.5 text-right">
                <span className="block font-display text-2xl font-bold tabular-nums leading-none">
                  {item.stat}
                </span>
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold leading-snug text-foreground">{item.headline}</p>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{item.detail}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Demo CTA — visible in both teaser and unlocked phases */}
      <div className="rounded-2xl bg-brand-navy p-6 shadow-sm md:p-8">
        <div className="flex flex-wrap items-start justify-between gap-6">
          <div className="max-w-lg">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-brand-lime">
              Ready to act on this?
            </p>
            <h3 className="mt-2 font-display text-xl font-semibold text-white">
              Collect, rights-clear and publish this content as a shoppable gallery.
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-white/70">
              Idukki pulls every post your audit found, auto-requests usage rights from the creator, then embeds the approved posts as a shoppable gallery on your product pages — tagged to products with direct checkout links. No code needed.
            </p>
            <ul className="mt-3 space-y-1">
              {[
                'Works on Shopify, WooCommerce, BigCommerce and more',
                'Rights requests handled automatically via DM',
                'Tag products inside posts for one-click checkout',
              ].map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm text-white/70">
                  <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-lime" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
            <Button
              asChild
              size="lg"
              className="bg-brand-lime font-semibold text-brand-navy hover:bg-brand-lime/90 focus-visible:ring-2 focus-visible:ring-white"
            >
              <a href="https://app.idukki.io/register?source=brand-audit-cta" target="_blank" rel="noopener noreferrer">
                Start free <ArrowRight className="ml-1.5 h-4 w-4" />
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-white/25 bg-transparent text-white hover:bg-white/10 hover:text-white"
            >
              <a href="https://idukki.io/contact?source=brand-audit-demo" target="_blank" rel="noopener noreferrer">
                Talk to us →
              </a>
            </Button>
          </div>
        </div>
      </div>

      {/* Debug panel — visible only with ?idk_dbg=1 */}
      {isDebug && rawApiData && (
        <div className="rounded-2xl border border-amber-400/50 bg-amber-50/30 p-5 text-foreground shadow-sm dark:bg-amber-950/20 md:p-6">
          <div className="mb-3 flex items-center gap-2">
            <span className="rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-white">
              DEBUG · ?idk_dbg=1
            </span>
            <span className="text-xs text-muted-foreground">
              API response in {scanDurationMs != null ? `${scanDurationMs}ms` : '—'}
            </span>
          </div>
          <div className="space-y-3 text-xs">
            <div className="rounded-lg border border-border/50 bg-card p-3">
              <p className="mb-1.5 font-bold uppercase tracking-wider text-muted-foreground">Data source</p>
              <p className="font-mono">
                {(rawApiData as Record<string, unknown>)?._debug
                  ? `${((rawApiData as Record<string, unknown>)._debug as Record<string, unknown>).source}`
                  : 'simulation (no _debug field in response)'}
              </p>
              {Boolean((rawApiData as Record<string, unknown>)?._debug) && (() => {
                const dbg = (rawApiData as Record<string, unknown>)._debug as Record<string, unknown>
                const calls = Array.isArray(dbg.rapidApiCalls) ? dbg.rapidApiCalls as Record<string, unknown>[] : null
                return (
                  <div className="mt-2 space-y-1 font-mono text-muted-foreground">
                    <p>
                      RapidAPI configured:{' '}
                      <span className={cn('font-semibold', dbg.rapidApiConfigured ? 'text-emerald-600' : 'text-rose-600')}>
                        {String(Boolean(dbg.rapidApiConfigured))}
                      </span>
                      {dbg.rapidApiHost != null && <> · host: <span className="text-foreground">{String(dbg.rapidApiHost)}</span></>}
                    </p>
                    {dbg.rapidApiError != null && (
                      <p className="text-rose-600">{String(dbg.rapidApiError)}</p>
                    )}
                    {calls && calls.length > 0 && (
                      <div className="mt-1 space-y-0.5 border-l-2 border-border/60 pl-2">
                        {calls.map((c, i) => (
                          <p key={i}>
                            <span className={cn('font-semibold', c.status === 200 ? 'text-emerald-600' : 'text-rose-600')}>
                              {String(c.status)}
                            </span>{' '}
                            {String(c.url)} {c.items != null && <>— {String(c.items)} items</>}
                            {c.note != null && <> — {String(c.note)}</>}
                          </p>
                        ))}
                      </div>
                    )}
                    {dbg.graphqlCalled === true && (
                      <>
                        <p>GraphQL URL: <span className="text-foreground">{String(dbg.graphqlUrl ?? '—')}</span></p>
                        <p>HTTP status: <span className={cn('font-semibold', dbg.graphqlStatus === 200 ? 'text-emerald-600' : 'text-rose-600')}>{String(dbg.graphqlStatus ?? '—')}</span></p>
                        <p>Success: <span className={cn('font-semibold', dbg.graphqlSuccess ? 'text-emerald-600' : 'text-rose-600')}>{String(dbg.graphqlSuccess)}</span></p>
                        {dbg.graphqlError && <p>Error: <span className="text-rose-600">{String(dbg.graphqlError)}</span></p>}
                      </>
                    )}
                    {dbg.graphqlCalled === false && (
                      <p className="text-amber-600">GraphQL not called — no API URL configured</p>
                    )}
                  </div>
                )
              })()}
            </div>
            {(() => {
              const dbg = (rawApiData as Record<string, unknown>)?._debug as Record<string, unknown> | undefined
              const classified = Array.isArray(dbg?.rapidApiPosts)
                ? dbg!.rapidApiPosts as Array<{
                    id: string; handle: string; category: Exclude<Category, 'all'>; reason: string
                    source: string; likes: number; takenAt: number; caption: string; permalink?: string
                  }>
                : null
              if (!classified || classified.length === 0) return null
              const groups: Exclude<Category, 'all'>[] = ['customer', 'influencer', 'employee', 'brand', 'brand_repost']
              function downloadAllClassifiedCSV() {
                const rows = [
                  ['Handle', 'Category', 'Source', 'Likes', 'Taken at (UTC)', 'Reason', 'Caption', 'Permalink'],
                  ...classified!.map((p) => [
                    p.handle,
                    CATEGORY_META[p.category].label,
                    p.source,
                    String(p.likes),
                    p.takenAt ? new Date(p.takenAt * 1000).toISOString() : '',
                    p.reason,
                    `"${p.caption.replace(/"/g, '""')}"`,
                    p.permalink ?? '',
                  ]),
                ]
                const csv = rows.map((r) => r.join(',')).join('\n')
                const blob = new Blob([csv], { type: 'text/csv' })
                const url = URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = url
                a.download = `${result?.slug ?? 'brand-audit'}-all-fetched-posts.csv`
                a.click()
                URL.revokeObjectURL(url)
              }
              return (
                <div className="rounded-lg border border-border/50 bg-card p-3">
                  <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2">
                    <p className="font-bold uppercase tracking-wider text-muted-foreground">
                      Every classified post ({classified.length}) — why each one landed in its category
                    </p>
                    <Button variant="outline" size="sm" onClick={downloadAllClassifiedCSV} className="h-6 gap-1 px-2 text-[10px]">
                      <Download className="h-3 w-3" /> Download all ({classified.length}) as CSV
                    </Button>
                  </div>
                  <div className="space-y-3">
                    {groups.map((cat) => {
                      const inCat = classified.filter((p) => p.category === cat)
                      if (inCat.length === 0) return null
                      return (
                        <div key={cat}>
                          <p className={cn('mb-1 inline-block rounded px-1.5 py-0.5 text-[10px] font-bold', CATEGORY_META[cat].bg, CATEGORY_META[cat].color)}>
                            {CATEGORY_META[cat].label} · {inCat.length}
                          </p>
                          <div className="mt-1 space-y-1 border-l-2 border-border/60 pl-2 font-mono text-muted-foreground">
                            {inCat.map((p) => (
                              <p key={p.id} className="break-all">
                                <span className="text-foreground">{p.handle}</span>
                                {' '}&middot; {p.likes} likes &middot; via {p.source}
                                <br />
                                <span className="text-[10px]">reason: {p.reason}</span>
                                {p.permalink && (
                                  <>
                                    {' '}·{' '}
                                    <a href={p.permalink} target="_blank" rel="noopener noreferrer" className="underline">
                                      view post
                                    </a>
                                  </>
                                )}
                              </p>
                            ))}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )
            })()}
            <div className="rounded-lg border border-border/50 bg-card p-3">
              <p className="mb-1.5 font-bold uppercase tracking-wider text-muted-foreground">Full API response</p>
              <pre className="overflow-x-auto whitespace-pre-wrap break-all font-mono text-[10px] leading-relaxed text-muted-foreground">
                {JSON.stringify(rawApiData, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      )}

      {phase === 'unlocked' && (
        <div className="rounded-2xl border bg-card shadow-sm">
          {/* Filter + sort bar */}
          <div className="flex flex-wrap items-center justify-between gap-3 border-b px-5 py-3 md:px-6">
            <div className="flex flex-wrap gap-1.5">
              {(['all', 'customer', 'influencer', 'employee', 'brand', 'brand_repost'] as Category[]).map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setFilterCat(cat)}
                  className={cn(
                    'rounded-full px-3 py-1 text-xs font-semibold transition',
                    filterCat === cat
                      ? 'bg-foreground text-background'
                      : 'bg-secondary text-muted-foreground hover:bg-secondary/80 hover:text-foreground'
                  )}
                  style={{ transition: `background var(--dur-fast) var(--ease-out)` }}
                >
                  {cat === 'all'
                    ? 'All'
                    : CATEGORY_META[cat].label.split(' ')[0]}
                </button>
              ))}
            </div>
            <div className="flex gap-1.5">
              {(['likes', 'recent'] as SortOrder[]).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setSortOrder(s)}
                  className={cn(
                    'rounded-full px-3 py-1 text-xs font-semibold transition',
                    sortOrder === s
                      ? 'bg-foreground text-background'
                      : 'bg-secondary text-muted-foreground hover:bg-secondary/80 hover:text-foreground'
                  )}
                  style={{ transition: `background var(--dur-fast) var(--ease-out)` }}
                >
                  {s === 'likes' ? 'Most liked' : 'Most recent'}
                </button>
              ))}
            </div>
          </div>

          {/* Post grid — this is always a sample (the highest-engagement posts
              this scan fetched, capped server-side), never the full analysed
              set. Say so plainly rather than implying it's everything. */}
          <div className="p-5 md:p-6">
            <p className="mb-4 text-xs text-muted-foreground">
              Showing a sample of {filteredPosts.length} real post{filteredPosts.length === 1 ? '' : 's'}
              {result.postsAnalysed > filteredPosts.length && <> out of {compact(result.postsAnalysed)} fetched</>}.
              Full post-by-post breakdown, rights requests and shoppable tagging happen inside Idukki.
            </p>
            {filteredPosts.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                No posts match this filter.
              </p>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3">
                {filteredPosts.map((post) => (
                  <PostTile key={post.id} post={post} />
                ))}
              </div>
            )}
          </div>

          {/* Export + share */}
          <div className="flex flex-wrap items-center gap-3 border-t px-5 py-4 md:px-6">
            <Button
              variant="outline"
              size="sm"
              onClick={exportCSV}
              className="gap-1.5"
            >
              <Download className="h-4 w-4" /> Export CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={copyShareLink}
              className="gap-1.5"
            >
              <Share2 className="h-4 w-4" /> Copy share link
            </Button>
            <Button asChild size="sm" className="ml-auto gap-1.5">
              <a href="https://app.idukki.io/register?source=brand-audit" target="_blank" rel="noopener noreferrer">
                Collect this UGC with Idukki <ArrowRight className="h-3.5 w-3.5" />
              </a>
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
